import React, { useState } from "react";
import {
  Sparkles,
  Copy,
  RotateCw,
  Loader2,
  MessageSquare,
  Megaphone,
  Image as ImageIcon,
  Users,
  CalendarClock,
} from "lucide-react";
import { Navbar } from "../components/app/Navbar";
import { Footer } from "../components/app/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useT } from "../context/LanguageContext";
import api from "../lib/api";
import { toast } from "sonner";

// ----- Reusable Output Language selector --------------------------------
// Each tool keeps its own selection so the user can flip between EN / ES
// content without touching the app's UI language. Default matches the
// current UI language so first-time users get sensible behavior.
const OutputLanguageSelect = ({ value, onChange, testid, t }) => (
  <div>
    <Label>{t("growth.outputLanguage")}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="en" data-testid={`${testid}-en`}>
          {t("growth.outputLanguage.en")}
        </SelectItem>
        <SelectItem value="es" data-testid={`${testid}-es`}>
          {t("growth.outputLanguage.es")}
        </SelectItem>
      </SelectContent>
    </Select>
  </div>
);

// ----- Shared small UI helpers ------------------------------------------

const ToolCard = ({ icon: Icon, title, subtitle, children, testid }) => (
  <section
    className="rounded-2xl bg-white border border-gray-200 shadow-sm overflow-hidden"
    data-testid={testid}
  >
    <header className="px-5 sm:px-6 py-4 border-b border-gray-100 flex items-start gap-3">
      <span className="w-10 h-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-emerald-700" />
      </span>
      <div className="min-w-0">
        <h2 className="font-display text-lg font-semibold text-gray-900 tracking-tight">
          {title}
        </h2>
        <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
      </div>
    </header>
    <div className="p-5 sm:p-6 space-y-4">{children}</div>
  </section>
);

const OutputBox = ({ text, secondary, testid, onCopy, onRegenerate, busy, t }) => {
  if (!text && !secondary) return null;
  return (
    <div
      className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4"
      data-testid={testid}
    >
      <pre
        className="whitespace-pre-wrap font-sans text-sm text-gray-900 leading-relaxed"
        data-testid={`${testid}-text`}
      >
        {text}
      </pre>
      {secondary ? (
        <div className="mt-3 pt-3 border-t border-emerald-200/60 space-y-2">
          {secondary}
        </div>
      ) : null}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCopy}
          className="border-emerald-200 bg-white text-emerald-900 hover:bg-emerald-50"
          data-testid={`${testid}-copy`}
        >
          <Copy className="w-3.5 h-3.5 mr-1.5" />
          {t("growth.copy")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onRegenerate}
          disabled={busy}
          className="border-gray-200 text-gray-700 hover:bg-gray-50"
          data-testid={`${testid}-regenerate`}
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <RotateCw className="w-3.5 h-3.5 mr-1.5" />
          )}
          {t("growth.regenerate")}
        </Button>
      </div>
    </div>
  );
};

const copyToClipboard = async (text, t) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(t("growth.copied"));
  } catch {
    toast.error(t("growth.copyFailed"));
  }
};

// ----- Tool 1: Review Reply Generator -----------------------------------

const ToolReviewReply = ({ t, lang }) => {
  const [review, setReview] = useState("");
  const [tone, setTone] = useState("friendly");
  const [outLang, setOutLang] = useState(lang || "en");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState("");

  const generate = async () => {
    if (!review.trim()) {
      toast.error(t("growth.review.errorEmpty"));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/growth-ai/review-reply", {
        review: review.trim(),
        tone,
        language: outLang,
      });
      setOut(res.data?.reply || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("growth.errorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ToolCard
      icon={MessageSquare}
      title={t("growth.review.title")}
      subtitle={t("growth.review.subtitle")}
      testid="growth-tool-review"
    >
      <div>
        <Label htmlFor="rr-review">{t("growth.review.inputLabel")}</Label>
        <Textarea
          id="rr-review"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder={t("growth.review.placeholder")}
          rows={3}
          data-testid="growth-review-input"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{t("growth.tone")}</Label>
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger data-testid="growth-review-tone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="friendly">{t("growth.tone.friendly")}</SelectItem>
              <SelectItem value="professional">{t("growth.tone.professional")}</SelectItem>
              <SelectItem value="warm">{t("growth.tone.warm")}</SelectItem>
              <SelectItem value="premium">{t("growth.tone.premium")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <OutputLanguageSelect
          value={outLang}
          onChange={setOutLang}
          testid="growth-review-output-language"
          t={t}
        />
      </div>
      <Button
        type="button"
        onClick={generate}
        disabled={busy}
        className="bg-green-800 hover:bg-green-900 text-white w-full sm:w-auto"
        data-testid="growth-review-generate"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("growth.generating")}
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            {t("growth.generate")}
          </>
        )}
      </Button>
      <OutputBox
        text={out}
        testid="growth-review-output"
        onCopy={() => copyToClipboard(out, t)}
        onRegenerate={generate}
        busy={busy}
        t={t}
      />
    </ToolCard>
  );
};

// ----- Tool 2: Google Business Post Generator ---------------------------

const ToolGbpPost = ({ t, lang }) => {
  const [serviceType, setServiceType] = useState("deep_cleaning");
  const [customService, setCustomService] = useState("");
  const [city, setCity] = useState("");
  const [tone, setTone] = useState("friendly");
  const [offer, setOffer] = useState("");
  const [outLang, setOutLang] = useState(lang || "en");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState("");

  const generate = async () => {
    if (!city.trim()) {
      toast.error(t("growth.gbp.errorCity"));
      return;
    }
    if (serviceType === "custom" && !customService.trim()) {
      toast.error(t("growth.gbp.errorCustom"));
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/growth-ai/google-business-post", {
        service_type: serviceType,
        custom_service: serviceType === "custom" ? customService.trim() : null,
        city: city.trim(),
        tone,
        special_offer: offer.trim() || null,
        language: outLang,
      });
      setOut(res.data?.post || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("growth.errorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ToolCard
      icon={Megaphone}
      title={t("growth.gbp.title")}
      subtitle={t("growth.gbp.subtitle")}
      testid="growth-tool-gbp"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{t("growth.serviceType")}</Label>
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger data-testid="growth-gbp-service">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="deep_cleaning">{t("growth.service.deep")}</SelectItem>
              <SelectItem value="move_out_cleaning">{t("growth.service.moveOut")}</SelectItem>
              <SelectItem value="recurring_cleaning">{t("growth.service.recurring")}</SelectItem>
              <SelectItem value="office_cleaning">{t("growth.service.office")}</SelectItem>
              <SelectItem value="custom">{t("growth.service.custom")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="gbp-city">{t("growth.city")}</Label>
          <Input
            id="gbp-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t("growth.cityPlaceholder")}
            data-testid="growth-gbp-city"
          />
        </div>
      </div>
      {serviceType === "custom" ? (
        <div>
          <Label htmlFor="gbp-custom">{t("growth.customService")}</Label>
          <Input
            id="gbp-custom"
            value={customService}
            onChange={(e) => setCustomService(e.target.value)}
            placeholder={t("growth.customServicePlaceholder")}
            data-testid="growth-gbp-custom"
          />
        </div>
      ) : null}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{t("growth.tone")}</Label>
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger data-testid="growth-gbp-tone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="friendly">{t("growth.tone.friendly")}</SelectItem>
              <SelectItem value="professional">{t("growth.tone.professional")}</SelectItem>
              <SelectItem value="promotional">{t("growth.tone.promotional")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="gbp-offer">{t("growth.gbp.offer")}</Label>
          <Input
            id="gbp-offer"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            placeholder={t("growth.gbp.offerPlaceholder")}
            data-testid="growth-gbp-offer"
          />
        </div>
      </div>
      <OutputLanguageSelect
        value={outLang}
        onChange={setOutLang}
        testid="growth-gbp-output-language"
        t={t}
      />
      <Button
        type="button"
        onClick={generate}
        disabled={busy}
        className="bg-green-800 hover:bg-green-900 text-white"
        data-testid="growth-gbp-generate"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("growth.generating")}
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            {t("growth.generate")}
          </>
        )}
      </Button>
      <OutputBox
        text={out}
        testid="growth-gbp-output"
        onCopy={() => copyToClipboard(out, t)}
        onRegenerate={generate}
        busy={busy}
        t={t}
      />
    </ToolCard>
  );
};

// ----- Tool 3: Before / After Caption Generator -------------------------

const ToolBeforeAfter = ({ t, lang }) => {
  const [serviceType, setServiceType] = useState("deep_cleaning");
  const [customService, setCustomService] = useState("");
  const [city, setCity] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [review, setReview] = useState("");
  const [outLang, setOutLang] = useState(lang || "en");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const generate = async () => {
    if (!city.trim()) {
      toast.error(t("growth.gbp.errorCity"));
      return;
    }
    if (serviceType === "custom" && !customService.trim()) {
      toast.error(t("growth.gbp.errorCustom"));
      return;
    }
    setBusy(true);
    try {
      // has_before_image / has_after_image are intentionally false now —
      // the tool no longer accepts images. The backend prompt already
      // tolerates this and produces copy without claiming knowledge of
      // any photo.
      const res = await api.post("/growth-ai/before-after-caption", {
        service_type: serviceType,
        custom_service: serviceType === "custom" ? customService.trim() : null,
        city: city.trim(),
        customer_review: review.trim() || null,
        platform,
        language: outLang,
        has_before_image: false,
        has_after_image: false,
      });
      setOut(res.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("growth.errorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  const fullText = out
    ? [out.hook, out.caption, out.cta, (out.hashtags || []).join(" ")]
        .filter(Boolean)
        .join("\n\n")
    : "";

  return (
    <ToolCard
      icon={ImageIcon}
      title={t("growth.ba.title")}
      subtitle={t("growth.ba.subtitle")}
      testid="growth-tool-ba"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{t("growth.serviceType")}</Label>
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger data-testid="growth-ba-service">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="deep_cleaning">{t("growth.service.deep")}</SelectItem>
              <SelectItem value="move_out_cleaning">{t("growth.service.moveOut")}</SelectItem>
              <SelectItem value="recurring_cleaning">{t("growth.service.recurring")}</SelectItem>
              <SelectItem value="office_cleaning">{t("growth.service.office")}</SelectItem>
              <SelectItem value="custom">{t("growth.service.custom")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="ba-city">{t("growth.city")}</Label>
          <Input
            id="ba-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t("growth.cityPlaceholder")}
            data-testid="growth-ba-city"
          />
        </div>
      </div>
      {serviceType === "custom" ? (
        <div>
          <Label htmlFor="ba-custom">{t("growth.customService")}</Label>
          <Input
            id="ba-custom"
            value={customService}
            onChange={(e) => setCustomService(e.target.value)}
            data-testid="growth-ba-custom"
          />
        </div>
      ) : null}
      <div>
        <Label>{t("growth.ba.platform")}</Label>
        <Select value={platform} onValueChange={setPlatform}>
          <SelectTrigger data-testid="growth-ba-platform">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="instagram">Instagram</SelectItem>
            <SelectItem value="facebook">Facebook</SelectItem>
            <SelectItem value="google_business">{t("growth.ba.googleBusiness")}</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label htmlFor="ba-review">{t("growth.ba.customerReview")}</Label>
        <Textarea
          id="ba-review"
          value={review}
          onChange={(e) => setReview(e.target.value)}
          placeholder={t("growth.ba.reviewPlaceholder")}
          rows={2}
          data-testid="growth-ba-review"
        />
      </div>
      <OutputLanguageSelect
        value={outLang}
        onChange={setOutLang}
        testid="growth-ba-output-language"
        t={t}
      />
      <Button
        type="button"
        onClick={generate}
        disabled={busy}
        className="bg-green-800 hover:bg-green-900 text-white"
        data-testid="growth-ba-generate"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("growth.generating")}
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            {t("growth.generate")}
          </>
        )}
      </Button>
      {out ? (
        <OutputBox
          text={out.caption}
          secondary={
            <>
              {out.hook ? (
                <div data-testid="growth-ba-hook">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
                    {t("growth.ba.hookLabel")}
                  </p>
                  <p className="text-sm text-gray-800 mt-0.5">{out.hook}</p>
                </div>
              ) : null}
              {out.cta ? (
                <div data-testid="growth-ba-cta">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
                    {t("growth.ba.ctaLabel")}
                  </p>
                  <p className="text-sm text-gray-800 mt-0.5">{out.cta}</p>
                </div>
              ) : null}
              {out.hashtags && out.hashtags.length > 0 ? (
                <div data-testid="growth-ba-hashtags">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
                    {t("growth.ba.hashtagsLabel")}
                  </p>
                  <p className="text-sm text-emerald-800 mt-0.5">
                    {out.hashtags.join(" ")}
                  </p>
                </div>
              ) : null}
            </>
          }
          testid="growth-ba-output"
          onCopy={() => copyToClipboard(fullText, t)}
          onRegenerate={generate}
          busy={busy}
          t={t}
        />
      ) : null}
    </ToolCard>
  );
};

// ----- Tool 4: Referral Campaign Generator -----------------------------

const ToolReferralCampaign = ({ t, lang }) => {
  const [businessName, setBusinessName] = useState("");
  const [offer, setOffer] = useState("");
  const [tone, setTone] = useState("friendly");
  const [outLang, setOutLang] = useState(lang || "en");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("/growth-ai/referral-campaign", {
        business_name: businessName.trim() || null,
        referral_offer: offer.trim() || null,
        tone,
        language: outLang,
      });
      setOut(res.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("growth.errorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  // Concatenate all three channels (with labels) for the single "Copy"
  // button at the bottom of the output. Owners typically copy/paste one
  // channel at a time though, so each card also has its own copy button.
  const fullText = out
    ? [
        out.sms ? `${t("growth.ref.smsLabel")}:\n${out.sms}` : "",
        out.email
          ? `${t("growth.ref.emailLabel")}${out.email_subject ? ` — ${out.email_subject}` : ""}:\n${out.email}`
          : "",
        out.facebook ? `${t("growth.ref.facebookLabel")}:\n${out.facebook}` : "",
      ]
        .filter(Boolean)
        .join("\n\n")
    : "";

  // Reuse the existing OutputBox's `secondary` slot for the email +
  // facebook cards so all three sit inside a single emerald container.
  // The SMS is shown via the main `text` prop because it's the shortest
  // and most-frequently-used channel.
  return (
    <ToolCard
      icon={Users}
      title={t("growth.ref.title")}
      subtitle={t("growth.ref.subtitle")}
      testid="growth-tool-referral"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="ref-biz">{t("growth.ref.businessName")}</Label>
          <Input
            id="ref-biz"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder={t("growth.ref.businessNamePlaceholder")}
            data-testid="growth-ref-business-name"
          />
        </div>
        <div>
          <Label htmlFor="ref-offer">{t("growth.ref.offer")}</Label>
          <Input
            id="ref-offer"
            value={offer}
            onChange={(e) => setOffer(e.target.value)}
            placeholder={t("growth.ref.offerPlaceholder")}
            data-testid="growth-ref-offer"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>{t("growth.tone")}</Label>
          <Select value={tone} onValueChange={setTone}>
            <SelectTrigger data-testid="growth-ref-tone">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="friendly">{t("growth.tone.friendly")}</SelectItem>
              <SelectItem value="professional">{t("growth.tone.professional")}</SelectItem>
              <SelectItem value="promotional">{t("growth.tone.promotional")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <OutputLanguageSelect
          value={outLang}
          onChange={setOutLang}
          testid="growth-ref-output-language"
          t={t}
        />
      </div>
      <Button
        type="button"
        onClick={generate}
        disabled={busy}
        className="bg-green-800 hover:bg-green-900 text-white w-full sm:w-auto"
        data-testid="growth-ref-generate"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("growth.generating")}
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            {t("growth.generate")}
          </>
        )}
      </Button>
      {out ? (
        <OutputBox
          text={out.sms}
          secondary={
            <>
              {out.email ? (
                <div data-testid="growth-ref-email">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
                    {t("growth.ref.emailLabel")}
                  </p>
                  {out.email_subject ? (
                    <p className="text-sm text-emerald-900 mt-0.5">
                      <span className="font-medium">{t("growth.ref.emailSubjectLabel")}:</span>{" "}
                      {out.email_subject}
                    </p>
                  ) : null}
                  <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 mt-1 leading-relaxed">
                    {out.email}
                  </pre>
                </div>
              ) : null}
              {out.facebook ? (
                <div data-testid="growth-ref-facebook">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">
                    {t("growth.ref.facebookLabel")}
                  </p>
                  <p className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap">
                    {out.facebook}
                  </p>
                </div>
              ) : null}
            </>
          }
          testid="growth-ref-output"
          onCopy={() => copyToClipboard(fullText, t)}
          onRegenerate={generate}
          busy={busy}
          t={t}
        />
      ) : null}
    </ToolCard>
  );
};

// ----- Tool 5: Slow Season Idea Generator ------------------------------

const ToolSlowSeason = ({ t, lang }) => {
  const [context, setContext] = useState("");
  const [city, setCity] = useState("");
  const [outLang, setOutLang] = useState(lang || "en");
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post("/growth-ai/slow-season-ideas", {
        business_context: context.trim() || null,
        city: city.trim() || null,
        language: outLang,
      });
      setOut(res.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("growth.errorGeneric"));
    } finally {
      setBusy(false);
    }
  };

  const ideas = (out && Array.isArray(out.ideas) ? out.ideas : []).filter(
    (it) => it && (it.title || it.description)
  );

  // For the "Copy" button: produce a clean, paste-ready bullet list.
  const fullText = ideas
    .map((it, i) => {
      const head = (it.title || "").trim();
      const body = (it.description || "").trim();
      const prefix = `${i + 1}.`;
      if (head && body) return `${prefix} ${head} — ${body}`;
      return `${prefix} ${head || body}`;
    })
    .join("\n\n");

  return (
    <ToolCard
      icon={CalendarClock}
      title={t("growth.slow.title")}
      subtitle={t("growth.slow.subtitle")}
      testid="growth-tool-slow"
    >
      <div>
        <Label htmlFor="slow-context">{t("growth.slow.context")}</Label>
        <Textarea
          id="slow-context"
          value={context}
          onChange={(e) => setContext(e.target.value)}
          placeholder={t("growth.slow.contextPlaceholder")}
          rows={3}
          data-testid="growth-slow-context"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label htmlFor="slow-city">{t("growth.slow.cityLabel")}</Label>
          <Input
            id="slow-city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t("growth.cityPlaceholder")}
            data-testid="growth-slow-city"
          />
        </div>
        <OutputLanguageSelect
          value={outLang}
          onChange={setOutLang}
          testid="growth-slow-output-language"
          t={t}
        />
      </div>
      <Button
        type="button"
        onClick={generate}
        disabled={busy}
        className="bg-green-800 hover:bg-green-900 text-white w-full sm:w-auto"
        data-testid="growth-slow-generate"
      >
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t("growth.generating")}
          </>
        ) : (
          <>
            <Sparkles className="w-4 h-4 mr-2" />
            {t("growth.generate")}
          </>
        )}
      </Button>
      {ideas.length > 0 ? (
        <div
          className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4"
          data-testid="growth-slow-output"
        >
          <p className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700 mb-2">
            {t("growth.slow.ideasLabel")}
          </p>
          <ol className="space-y-3" data-testid="growth-slow-output-list">
            {ideas.map((it, idx) => (
              <li
                key={`idea-${idx}`}
                className="flex gap-3"
                data-testid={`growth-slow-idea-${idx}`}
              >
                <span className="shrink-0 w-7 h-7 rounded-full bg-emerald-100 text-emerald-800 text-sm font-semibold flex items-center justify-center">
                  {idx + 1}
                </span>
                <div className="min-w-0">
                  {it.title ? (
                    <p className="text-sm font-semibold text-gray-900 leading-snug">
                      {it.title}
                    </p>
                  ) : null}
                  {it.description ? (
                    <p className="text-sm text-gray-700 mt-0.5 leading-relaxed whitespace-pre-wrap">
                      {it.description}
                    </p>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => copyToClipboard(fullText, t)}
              className="border-emerald-200 bg-white text-emerald-900 hover:bg-emerald-50"
              data-testid="growth-slow-output-copy"
            >
              <Copy className="w-3.5 h-3.5 mr-1.5" />
              {t("growth.copy")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={generate}
              disabled={busy}
              className="border-gray-200 text-gray-700 hover:bg-gray-50"
              data-testid="growth-slow-output-regenerate"
            >
              {busy ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <RotateCw className="w-3.5 h-3.5 mr-1.5" />
              )}
              {t("growth.regenerate")}
            </Button>
          </div>
        </div>
      ) : null}
    </ToolCard>
  );
};

// ----- Page wrapper -----------------------------------------------------

export default function GrowthAI() {
  const { t, lang } = useT();

  return (
    <div className="min-h-screen flex flex-col bg-[#f9f9f8]" data-testid="growth-ai-page">
      <Navbar />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="mb-8 flex items-start gap-3">
          <span className="w-12 h-12 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
            <Sparkles className="w-6 h-6 text-emerald-700" />
          </span>
          <div>
            <h1
              className="font-display text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight"
              data-testid="growth-ai-title"
            >
              {t("growth.pageTitle")}
            </h1>
            <p className="mt-2 text-gray-500 max-w-2xl">{t("growth.pageSubtitle")}</p>
          </div>
        </div>

        <div className="space-y-6">
          <ToolReviewReply t={t} lang={lang} />
          <ToolGbpPost t={t} lang={lang} />
          <ToolBeforeAfter t={t} lang={lang} />
          <ToolReferralCampaign t={t} lang={lang} />
          <ToolSlowSeason t={t} lang={lang} />
        </div>
      </main>
      <Footer />
    </div>
  );
}
